/**
 * Returns the last two parts of the domain, ie `fandom.com` for `starwars.fandom.com`. If the
 * domain is less than two parts then the whole hostname is returned, like `localhost`.
 *
 * @returns The top level domain for the current page, ie: the last two parts of the domain.
 */
export const getTopLevelDomain = () => {
  const hostname = window.location.hostname;
  const parts = hostname.split(".");

  if (parts.length < 2) {
    return hostname;
  }

  const topLevelDomain = `.${parts[parts.length - 2]}.${parts[parts.length - 1]}`;

  return topLevelDomain;
};

/**
 * This will take the hostname and split on `.`, taking the first part. Generally this is the
 * subdomain that is the specific wiki on `<wiki>.fandom.com`.
 *
 * For example for the hostname starwars.fandom.com this function will return starwars. For
 * fandom.com it will return fandom, and for localhost it will return localhost.
 *
 * For test pages, previews, sandbox, etc... it will return the firt subdomain of the url instead.
 * For example, `project44.sandbox-adeng02.fandom.com` will return `sanbox-adeng02` instead of the
 * wiki name `project44`.
 *
 * The following special urls will be handled in this way, returning the first subdomain part before
 * the fandom.com instead of the wiki name:
 *
 * - `<wiki>.showcase.fandom.com`
 * - `<wiki>.externaltest.fandom.com`
 * - `<wiki>.preview.fandom.com`
 * - `<wiki>.verify.fandom.com`
 * - `<wiki>.stable.fandom.com`
 * - `<wiki>.sandbox-<name>.fandom.com`
 *
 * @returns The first part of the hostname, or null if the hostname is empty.
 */
export function getHostnamePrefix(): string | null {
  const hostname = window.location.hostname.toLowerCase();

  // If the domain is a test or preview domain, return that part of the domain instead so it targets
  // as `showcase` or `externaltest` instead of the actual wiki part in match[1].
  const match =
    /(^|.)(showcase|externaltest|preview|verify|stable|sandbox-[^.]+)\./.exec(
      hostname,
    );
  if (match && match.length > 2) {
    return match[2];
  }

  // This is a normal domain so take the first part of the domain, ie `starwars` in
  // `starwars.fandom.com`. If the hostname is an empty string, then return null.
  const [firstPart] = hostname.split(".");
  return firstPart || null;
}
